//  ----------      START   IMPORT  BLOCK       ----------
import APIService from '../Core/APIService';
import API_ROUTES from '../../ConfigFiles/Routes/api_routes';
import type { 
    UserRead, 
    PaginatedMatchingUser, 
    PatientDetailsResponse,
    PartialGeneralInfoUpdate, 
    PartialProfessionalProfileUpdate,
    WorkLocationAndAssignmentUpdate,
    WorkAssignmentRead,
    UserProfilePartialUpdate
} from '../../Types/schema-helpers';
//  ----------      END   IMPORT  BLOCK       ----------

export interface SearchMedicParams {
    query?: string;
    speciality?: string;
    skill_ids?: string[];
    skip?: number;
    limit?: number;
}

/**
 * Service dedicated to user identity, profile fetching, and searching medics/patients.
 */
class UserService extends APIService {

    /**
     * Fetch the current authenticated user's AUTH PROFILE (UserRead).
     * Used by SessionContext to check credentials, roles, and status.
     */
    async fetchCurrentUser(): Promise<UserRead | null> {
        try {
            // Points to auth user endpoint (e.g. /users/me or /auth/me)
            const userData = await this.get<UserRead>(API_ROUTES.search_me); 
            return (userData && Object.keys(userData).length > 0) ? userData : null;
        } catch (error: any) {
            if (error.response?.status === 401) return null;
            throw error;
        }
    }

    /**
     * Fetch a patient's CLINICAL RECORD (PatientDetailsResponse).
     * When query is 'me', hits API_ROUTES.search_me to retrieve the full medical summary.
     */
    async searchPatient(query: string): Promise<PatientDetailsResponse | null> {
        try {
            // 1. If query is 'me', hit search_me for clinical records
            const route = (query === 'me') 
                ? API_ROUTES.search_me 
                : API_ROUTES.search_patient(query);

            const response = await this.get<PatientDetailsResponse>(route);
            return response || null;
        } catch (error: any) {
            console.error('Error searching patient:', error);
            throw error;
        }
    }

    /**
     * Partially update core profile details (general_info, contact, addresses, emergency_contacts).
     * Endpoint: PATCH /user/profile
     */
    async updateUserProfile(payload: UserProfilePartialUpdate): Promise<UserRead> {
        const route = API_ROUTES.update_user_profile();
        return this.patch<UserRead>(route, payload);
    }

    /**
   * Partially updates a patient's profile by professional.
   */
    async updatePatientInfoByPro(username: string, payload: PartialGeneralInfoUpdate) {
        const route = API_ROUTES.updatePatientInfoByPro(username);
        const response = await this.patch(route, payload);
        return response.data;
    }

    /**
   * Partially updates the current user's professional profile (specialty, subspecialty, skills).
   */
  async updateProInfoByPro(payload: PartialProfessionalProfileUpdate): Promise<UserRead> {
    const route = API_ROUTES.updateProInfoByPro();
    return this.patch<UserRead>(route, payload);
  }

  /**
     * Updates physical clinic details and operational consultation schedules in a single request.
     */
    async updateProWorkLocationAndAssignment(
        payload: WorkLocationAndAssignmentUpdate,
        locationIdOverride?: string
    ): Promise<WorkAssignmentRead> {
        const route = API_ROUTES.updateProWorkLocationAndAssignment(locationIdOverride);
        return this.patch<WorkAssignmentRead>(route, payload);
    }

    /**
     * Search for medical professionals with optional filters and pagination.
     */
    async searchMedic(params: SearchMedicParams = {}): Promise<PaginatedMatchingUser | null> {
        try {
            const {
                query = '',
                skip = 0,
                limit = 10
            } = params;

            const route = API_ROUTES.search_medic(query, skip, limit);
            const response = await this.get<PaginatedMatchingUser>(route);
            return response || null;
        } catch (error: any) {
            console.error('Error searching medic:', error);
            throw error;
        }
    }
}

export default new UserService();